import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchTemplates } from '../api/client';
import TemplateCard from '../components/TemplateCard';
import SkeletonCard from '../components/SkeletonCard';
import CategoryPills from '../components/CategoryPills';
import FilterSidebar from '../components/FilterSidebar';
import { LayoutGrid, Grid, List, Sparkles, X, SlidersHorizontal } from 'lucide-react';

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || 'All';
  const q = searchParams.get('q') || '';
  const price = searchParams.get('price') || '';
  const minRating = searchParams.get('min_rating') || '';
  const sort = searchParams.get('sort') || 'newest';
  const format = searchParams.get('format') || '';

  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'compact' | 'list'
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['templates', { category, q, price, minRating, sort, format }],
    queryFn: () => fetchTemplates({ category, q, price, minRating, sort, format }),
  });

  const handleCategorySelect = (newCategory) => {
    const params = new URLSearchParams(searchParams);
    if (newCategory === 'All') {
      params.delete('category');
    } else {
      params.set('category', newCategory);
    }
    setSearchParams(params);
  };

  const handleFilterChange = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'clear') {
      params.delete('price');
      params.delete('min_rating');
      params.delete('format');
    } else {
      if (value) {
        if (key === 'minRating') params.set('min_rating', value);
        else params.set(key, value);
      } else {
        if (key === 'minRating') params.delete('min_rating');
        else params.delete(key);
      }
    }
    setSearchParams(params);
  };

  const handleSortChange = (e) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', e.target.value);
    setSearchParams(params);
  };

  const removeSpecificFilter = (paramKey) => {
    const params = new URLSearchParams(searchParams);
    params.delete(paramKey);
    setSearchParams(params);
  };

  const templates = data?.templates || [];

  return (
    <div className="page-container" style={{ paddingBottom: '100px', paddingTop: '32px' }}>
      
      {/* Header & Title */}
      <div style={{ marginBottom: '24px' }}>
        <h1 className="text-h1" style={{ marginBottom: '6px' }}>Explore Templates & Assets</h1>
        <p className="text-secondary" style={{ fontSize: '0.9375rem' }}>
          Discover curated design systems, UI kits, 3D mockups, and creative kits by world-class creators.
        </p>
      </div>
      
      {/* Category Pills Bar */}
      <div style={{ marginBottom: '28px' }}>
        <CategoryPills activeCategory={category} onSelect={handleCategorySelect} />
      </div>

      {/* Active Filter Chips Bar */}
      {(price || minRating || format || q) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Active Filters:</span>
          {q && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--bg-surface)', padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', border: '1px solid var(--border-subtle)' }}>
              Search: "{q}"
              <button onClick={() => removeSpecificFilter('q')} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 0 }}><X size={12} /></button>
            </span>
          )}
          {price && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--bg-surface)', padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', border: '1px solid var(--border-subtle)' }}>
              Price: {price === 'free' ? 'Free Only' : 'Paid'}
              <button onClick={() => removeSpecificFilter('price')} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 0 }}><X size={12} /></button>
            </span>
          )}
          {format && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--bg-surface)', padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', border: '1px solid var(--border-subtle)' }}>
              Format: {format.toUpperCase()}
              <button onClick={() => removeSpecificFilter('format')} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 0 }}><X size={12} /></button>
            </span>
          )}
          <button
            onClick={() => handleFilterChange('clear')}
            style={{ background: 'none', border: 'none', color: 'var(--primary-text)', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
          >
            Clear All
          </button>
        </div>
      )}

      {/* Main Browse Container */}
      <div style={{ display: 'flex', gap: '32px' }}>
        
        {/* Sidebar */}
        <aside style={{ width: '260px', flexShrink: 0 }} className="hidden-mobile">
          <FilterSidebar 
            filters={{ price, minRating, format }} 
            onFilterChange={handleFilterChange} 
          />
        </aside>

        {/* Main Content Area */}
        <div style={{ flex: 1 }}>
          
          {/* Controls Bar: Total Found, View Mode Switcher, Sort Dropdown */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
              {isLoading ? 'Scanning inventory...' : `${data?.total || templates.length} assets ready for download`}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* View Switcher */}
              <div style={{
                display: 'flex',
                background: 'var(--bg-surface)',
                padding: '3px',
                borderRadius: '10px',
                border: '1px solid var(--border-subtle)'
              }}>
                <button
                  onClick={() => setViewMode('grid')}
                  title="Comfortable Grid"
                  className="tactile-btn"
                  style={{
                    padding: '6px 8px',
                    borderRadius: '7px',
                    background: viewMode === 'grid' ? 'var(--bg-surface-active)' : 'transparent',
                    color: viewMode === 'grid' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  onClick={() => setViewMode('compact')}
                  title="Compact Grid"
                  className="tactile-btn"
                  style={{
                    padding: '6px 8px',
                    borderRadius: '7px',
                    background: viewMode === 'compact' ? 'var(--bg-surface-active)' : 'transparent',
                    color: viewMode === 'compact' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <Grid size={16} />
                </button>
              </div>

              {/* Sort Menu */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Sort:</span>
                <select 
                  value={sort} 
                  onChange={handleSortChange}
                  style={{ 
                    padding: '8px 14px', 
                    borderRadius: '10px', 
                    border: '1px solid var(--border-subtle)',
                    backgroundColor: 'var(--bg-surface)',
                    color: 'var(--text-primary)',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value="newest">Newest Releases</option>
                  <option value="trending">Trending & Popular</option>
                  <option value="most_downloaded">Most Downloaded</option>
                  <option value="highest_rated">Highest Rated (5★)</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                </select>
              </div>
            </div>
          </div>

          {isError && (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--status-error)', background: 'var(--bg-surface)', borderRadius: '16px', border: '1px solid var(--border-subtle)' }}>
              Failed to load assets from network. Please check your connection.
            </div>
          )}

          {/* Dynamic Grid Layout */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: viewMode === 'compact' 
              ? 'repeat(auto-fill, minmax(220px, 1fr))' 
              : 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: viewMode === 'compact' ? '16px' : '24px'
          }}>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
            ) : templates.length > 0 ? (
              templates.map(t => <TemplateCard key={t.id} {...t} />)
            ) : (
              <div style={{
                gridColumn: '1 / -1',
                padding: '80px 20px',
                textAlign: 'center',
                background: 'var(--bg-surface)',
                borderRadius: '24px',
                border: '1px solid var(--border-subtle)'
              }}>
                <Sparkles size={40} color="var(--primary)" style={{ margin: '0 auto 16px' }} />
                <h3 className="text-h3" style={{ marginBottom: '8px' }}>No matching templates found</h3>
                <p className="text-secondary" style={{ fontSize: '0.875rem', marginBottom: '20px' }}>
                  Try relaxing your search terms or clearing selected filter tags.
                </p>
                <button
                  onClick={() => handleFilterChange('clear')}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '10px',
                    background: 'var(--primary-subtle)',
                    color: 'var(--primary-text)',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    border: '1px solid var(--primary-border)',
                    cursor: 'pointer'
                  }}
                >
                  Reset All Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
